import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { addLeadNote, autoAssignLead, getLead, listLeadEvents, reassignLead, setLeadStage, setNextStep, simulateTimePassing } from '../lib/api';
import {
  STAGES, countdown, dateTime, formatPhone, friendlyError, fromLocalInput, inr, isOverdue, lostReasonLabel, relative, sourceLabel,
  stageLabel, stageTone, toLocalInput,
} from '../lib/format';
import { actorName, describeEvent } from '../lib/timeline';
import type { Lead, LeadEvent, LostReason, Stage } from '../lib/types';
import { Badge, Button, ErrorText, SelectField, TextArea, TextField } from '../components/ui';
import { ChatPanel } from '../components/ChatPanel';
import { CallLogForm } from '../components/CallLogForm';
import { VisitsPanel } from '../components/VisitsPanel';
import { RegistrationPanel } from '../components/RegistrationPanel';
import { BookingPanel } from '../components/BookingPanel';

type Panel = null | 'touch' | 'next' | 'stage' | 'note' | 'reassign';

export function LeadDrawer({ leadId, onClose, onChanged }: { leadId: string; onClose: () => void; onChanged: () => void }) {
  const { members, projects, isManager } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [panel, setPanel] = useState<Panel>(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [clockNote, setClockNote] = useState('');

  const nameOf = useCallback((id: string | null | undefined) => members.find((m) => m.id === id)?.full_name ?? (id ? 'Former member' : 'Unassigned'), [members]);

  const load = useCallback(async () => {
    try {
      const [l, ev] = await Promise.all([getLead(leadId), listLeadEvents(leadId)]);
      setLead(l); setEvents(ev);
      if (!l) setLoadError("You don't have access to this lead.");
    } catch (e) {
      setLoadError(friendlyError(e));
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const done = async () => { setPanel(null); await load(); onChanged(); };
  const project = projects.find((p) => p.id === lead?.project_id);
  const phone = lead?.buyer?.phone_e164 ?? '';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim" onMouseDown={onClose}>
      <aside role="dialog" aria-modal="true" aria-label="Lead details"
        className="flex h-full w-full max-w-[560px] flex-col overflow-y-auto bg-surface shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <p className="font-mono text-xs text-muted">L-{lead?.ref_no ?? '…'}</p>
            <h2 className="truncate font-display text-xl">{lead?.buyer?.name || (phone ? formatPhone(phone) : 'Lead')}</h2>
            {lead && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge tone={stageTone[lead.stage]}>{stageLabel[lead.stage]}</Badge>
                {lead.is_test && <Badge tone="warn">Test</Badge>}
                {lead.buyer?.is_nri && <Badge tone="cold">NRI</Badge>}
                {lead.lost_reason && <Badge tone="bad">{lostReasonLabel[lead.lost_reason]}</Badge>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded-md px-2 text-2xl leading-none text-muted hover:text-ink">×</button>
        </div>

        {loadError && <div className="p-5"><ErrorText>{loadError}</ErrorText></div>}
        {lead?.needs_human_at && (
          <div className="border-b border-bad bg-badSoft px-5 py-3 text-sm text-bad">
            <strong>Needs you:</strong> {lead.needs_human_reason ?? 'A person should reply.'} Reply or log a call to clear this.
          </div>
        )}

        {lead && (() => { const cd = countdown(lead.response_due_at); return cd && (
          <div className={`border-b px-5 py-2 text-sm ${cd.late ? 'border-bad bg-badSoft text-bad' : 'border-warn bg-warnSoft text-warn'}`}>
            <strong>{cd.text}</strong> · {nameOf(lead.owner_id)} must call or message, or it moves to the next rep.
          </div>
        ); })()}

        {lead && (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 px-5 py-4 text-sm">
              <Item label="Phone">
                <a className="text-accent hover:underline" href={`tel:${phone}`}>{formatPhone(phone)}</a>
                {' · '}
                <a className="text-accent hover:underline" href={`https://wa.me/${phone.replace('+', '')}`} target="_blank" rel="noreferrer">WhatsApp</a>
              </Item>
              <Item label="Project">{project?.name ?? 'Not decided'}</Item>
              <Item label="Owner">
                {nameOf(lead.owner_id)}
                {lead.assign_reason && <span className="block text-xs text-muted">{lead.assign_reason}</span>}
                {!lead.owner_id && isManager && (
                  <button className="block text-xs text-accent hover:underline" onClick={async () => {
                    setActionError('');
                    try { await autoAssignLead(lead.id); await load(); onChanged(); } catch (e) { setActionError(friendlyError(e)); }
                  }}>Assign by the rules</button>
                )}
              </Item>
              <Item label="Source">{sourceLabel[lead.source]}{lead.source_detail ? ` · ${lead.source_detail}` : ''}{lead.source_code ? ` · ${lead.source_code}` : ''}</Item>
              <Item label="Came in">{dateTime(lead.created_at)}</Item>
              <Item label="First human touch">{lead.first_human_touch_at ? dateTime(lead.first_human_touch_at) : <span className="text-warn">Not yet</span>}</Item>
              {lead.sourced_by && <Item label="Sourced by">{nameOf(lead.sourced_by)}</Item>}
              {(lead.unit_type || lead.budget_max_inr || lead.horizon) && (
                <Item label="Looking for">{[lead.unit_type, lead.budget_max_inr && `up to ${inr(lead.budget_max_inr)}`, lead.horizon].filter(Boolean).join(' · ')}</Item>
              )}
              {lead.score !== 'unscored' && <Item label="Score">{lead.score[0].toUpperCase() + lead.score.slice(1)}{lead.score_reason ? ` · ${lead.score_reason}` : ''}</Item>}
              <div className="col-span-2">
                <dt className="text-xs text-muted">Next step</dt>
                <dd className={lead.next_step_at && isOverdue(lead.next_step_at) ? 'text-bad' : ''}>
                  {lead.next_step_at ? `${lead.next_step_note} · ${dateTime(lead.next_step_at)} (${relative(lead.next_step_at)})` : <span className="text-warn">None set</span>}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2 border-y border-line bg-surface2 px-5 py-3">
              <Button onClick={() => setPanel('touch')} variant={panel === 'touch' ? 'primary' : 'secondary'}>Log call</Button>
              <Button onClick={() => setPanel('next')} variant={panel === 'next' ? 'primary' : 'secondary'}>Next step</Button>
              <Button onClick={() => setPanel('stage')} variant={panel === 'stage' ? 'primary' : 'secondary'}>Stage</Button>
              <Button onClick={() => setPanel('note')} variant={panel === 'note' ? 'primary' : 'secondary'}>Note</Button>
              {isManager && <Button onClick={() => setPanel('reassign')} variant={panel === 'reassign' ? 'primary' : 'secondary'}>Reassign</Button>}
            </div>
            {actionError && <div className="px-5 pt-3"><ErrorText>{actionError}</ErrorText></div>}

            {panel && (
              <div className="border-b border-line px-5 py-4">
                {panel === 'touch' && <CallLogForm lead={lead} onDone={done} onCancel={() => setPanel(null)} />}
                {panel === 'next' && <NextStepForm lead={lead} onDone={done} onCancel={() => setPanel(null)} />}
                {panel === 'stage' && <StageForm lead={lead} onDone={done} onCancel={() => setPanel(null)} />}
                {panel === 'note' && <NoteForm lead={lead} onDone={done} onCancel={() => setPanel(null)} />}
                {panel === 'reassign' && <ReassignForm lead={lead} onDone={done} onCancel={() => setPanel(null)} />}
              </div>
            )}

            <ChatPanel lead={lead} nameOf={nameOf} onSent={() => { load(); onChanged(); }} />
            <RegistrationPanel lead={lead} onChanged={() => { load(); onChanged(); }} />
            <VisitsPanel lead={lead} onChanged={() => { load(); onChanged(); }} />
            {isManager && <BookingPanel lead={lead} onChanged={() => { load(); onChanged(); }} />}

            {lead.is_test && isManager && (
              <section className="border-b border-line px-5 py-4">
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">Test clock</h3>
                <p className="mb-2 text-sm text-muted">Pretend time has passed on this test lead to see the time limits work.</p>
                <div className="flex flex-wrap gap-2">
                  {[15, 60, 121].map((m) => (
                    <Button key={m} onClick={async () => {
                      setActionError(''); setClockNote('');
                      try { const n = await simulateTimePassing(lead.id, m); setClockNote(n ? 'Time limit missed: the lead moved on.' : 'Still within the time limit.'); await load(); onChanged(); }
                      catch (e) { setActionError(friendlyError(e)); }
                    }}>+{m} min</Button>
                  ))}
                </div>
                {clockNote && <p className="mt-2 text-sm text-ok">{clockNote}</p>}
              </section>
            )}

            <section className="px-5 py-4">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted">Timeline</h3>
              <ol className="flex flex-col gap-3">
                {events.map((e) => {
                  const d = describeEvent(e, nameOf);
                  return (
                    <li key={e.id} className="border-l-2 border-line pl-3">
                      <p className="text-sm font-medium">{d.title}</p>
                      {d.detail && <p className="whitespace-pre-wrap text-sm">{d.detail}</p>}
                      <p className="text-xs text-muted">{actorName(e, nameOf)} · {dateTime(e.created_at)}</p>
                    </li>
                  );
                })}
              </ol>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt className="text-xs text-muted">{label}</dt><dd>{children}</dd></div>;
}

type FormProps = { lead: Lead; onDone: () => Promise<void>; onCancel: () => void };

function useSubmit(action: () => Promise<void>, onDone: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { await action(); await onDone(); } catch (err) { setError(friendlyError(err)); } finally { setBusy(false); }
  };
  return { busy, error, submit };
}

function Actions({ busy, label, onCancel }: { busy: boolean; label: string; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : label}</Button>
    </div>
  );
}

function NextStepForm({ lead, onDone, onCancel }: FormProps) {
  const [at, setAt] = useState(toLocalInput(new Date(Date.now() + 24 * 3600e3)));
  const [note, setNote] = useState(lead.next_step_note ?? '');
  const { busy, error, submit } = useSubmit(() => setNextStep(lead.id, fromLocalInput(at), note), onDone);
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <TextField label="When (IST)" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} />
      <TextField label="What" required value={note} onChange={(e) => setNote(e.target.value)} placeholder="Send floor plan and confirm visit" />
      <ErrorText>{error}</ErrorText>
      <Actions busy={busy} label="Set next step" onCancel={onCancel} />
    </form>
  );
}

function StageForm({ lead, onDone, onCancel }: FormProps) {
  const [stage, setStage] = useState<Stage>(lead.stage);
  const [reason, setReason] = useState<LostReason | ''>('');
  const [wake, setWake] = useState('');
  const { busy, error, submit } = useSubmit(() => setLeadStage(lead.id, stage, reason || null, wake || null), onDone);
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <SelectField label="Stage" value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
        {STAGES.map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
      </SelectField>
      {stage === 'lost' && (
        <SelectField label="Why was it lost?" required value={reason} onChange={(e) => setReason(e.target.value as LostReason)}>
          <option value="">Choose a reason</option>
          {(Object.keys(lostReasonLabel) as LostReason[]).map((r) => <option key={r} value={r}>{lostReasonLabel[r]}</option>)}
        </SelectField>
      )}
      {stage === 'dormant' && <TextField label="Wake on" type="date" required value={wake} onChange={(e) => setWake(e.target.value)} />}
      <ErrorText>{error}</ErrorText>
      <Actions busy={busy} label="Save stage" onCancel={onCancel} />
    </form>
  );
}

function NoteForm({ lead, onDone, onCancel }: FormProps) {
  const [text, setText] = useState('');
  const { busy, error, submit } = useSubmit(() => addLeadNote(lead.id, text), onDone);
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <TextArea label="Note" required value={text} onChange={(e) => setText(e.target.value)} />
      <ErrorText>{error}</ErrorText>
      <Actions busy={busy} label="Add note" onCancel={onCancel} />
    </form>
  );
}

function ReassignForm({ lead, onDone, onCancel }: FormProps) {
  const { members } = useAuth();
  const [ownerId, setOwnerId] = useState('');
  const [reason, setReason] = useState('');
  const { busy, error, submit } = useSubmit(() => reassignLead(lead.id, ownerId, reason), onDone);
  const options = members.filter((m) => m.active && !m.on_leave && m.id !== lead.owner_id);
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <SelectField label="New owner" required value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
        <option value="">Choose someone</option>
        {options.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
      </SelectField>
      <TextField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Buyer prefers Hindi" />
      <ErrorText>{error}</ErrorText>
      <Actions busy={busy} label="Reassign" onCancel={onCancel} />
    </form>
  );
}
